import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { UserFilterListItem } from '@/models/user/userFilter';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { FilterState } from '@/types/filter';
import {
  buildGlobalChips,
  buildLocalSections,
  parseDateRange,
} from '@/components/filter/SaveFiltersDialog/SaveFiltersDialog.utils';
import FilterPreviewModal, { type FilterPreviewData } from '@/components/filter/FilterPreviewModal/FilterPreviewModal';
import { unwrapS3Response, validateS3Url } from '@/utils/filter/unwrapS3Response';
import { isRecord } from '@/utils/filter/isRecord';
import { isFilterState } from '@/utils/filter/isFilterState';

interface FilterDetailsDialogProps {
  isOpen: boolean;
  filter: UserFilterListItem | null;
  onClose: () => void;
}

const FilterDetailsDialog: FC<FilterDetailsDialogProps> = ({ isOpen, filter, onClose }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [parsedData, setParsedData] = useState<FilterPreviewData | null>(null);

  useEffect(() => {
    if (!isOpen || !filter) return;

    let cancelled = false;
    const fetchFilterDetails = async (filterId: string): Promise<void> => {
      setIsLoading(true);
      setParsedData(null);
      try {
        const detailResponse = await UserFilterApiService.getFilterById(filterId);
        if (cancelled) return;

        const { filters_download_link: downloadLink } = detailResponse.data;
        validateS3Url(downloadLink);

        const filtersResponse = await axios.get<Record<string, unknown>>(downloadLink);
        if (cancelled) return;

        const unwrapped = unwrapS3Response(filtersResponse.data);
        const nestedGlobalFilters = isFilterState(unwrapped.globalFilters) ? unwrapped.globalFilters : undefined;
        const globalFilters: FilterState = nestedGlobalFilters ?? (isFilterState(unwrapped) ? unwrapped : {});
        const localFilters =
          nestedGlobalFilters && isRecord(unwrapped.localFilters) ? unwrapped.localFilters : undefined;

        setParsedData({
          dateRangeText: parseDateRange(globalFilters, t),
          globalFilterChips: buildGlobalChips(globalFilters, t),
          localFilterSections: localFilters ? buildLocalSections(localFilters, t) : [],
        });
      } catch {
        if (!cancelled) setParsedData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchFilterDetails(filter.userFilterId);
    return () => {
      cancelled = true;
    };
  }, [isOpen, filter, t]);

  if (!filter) return null;

  return (
    <FilterPreviewModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('global.filters.manageSavedFilters.filterDetailsTitle')} ${filter.filterName}`}
      isLoading={isLoading}
      data={parsedData}
      data-testid="filter-details-dialog"
    />
  );
};

export default FilterDetailsDialog;
